"use client";

import React from "react";
import clsx from "clsx";

export type StatusPillVariant =
  | "success"
  | "warning"
  | "error"
  | "info"
  | "default";

export interface StatusPillProps {
  /**
   * The text content of the status pill
   */
  children: React.ReactNode;

  /**
   * The variant/type of status pill
   */
  variant?: StatusPillVariant;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Custom background color
   */
  backgroundColor?: string;

  /**
   * Custom text color
   */
  textColor?: string;
}

/**
 * StatusPill component - Matches Figma design specifications
 *
 * Features:
 * - Figma-accurate styling (20px border radius, 4px 12px padding)
 * - Multiple variants (success, warning, error, info, default)
 * - Custom color support
 * - Inter font family
 *
 * @example
 * ```tsx
 * <StatusPill variant="success">Active</StatusPill>
 * <StatusPill variant="warning">Draft</StatusPill>
 * <StatusPill variant="error">Delete</StatusPill>
 * <StatusPill variant="info">Info</StatusPill>
 * <StatusPill variant="default">Default</StatusPill>
 *
 * // Custom colors
 * <StatusPill backgroundColor="#e3f2fd" textColor="#1976d2">
 *   Custom
 * </StatusPill>
 * ```
 */
export const StatusPill: React.FC<StatusPillProps> = ({
  children,
  variant = "default",
  className,
  backgroundColor,
  textColor,
}) => {
  const getVariantStyles = (variant: StatusPillVariant) => {
    switch (variant) {
      case "success":
        return {
          backgroundColor: "rgba(102, 166, 0, 0.1)",
          color: "#66A600",
        };
      case "warning":
        return {
          backgroundColor: "#FFF7E6",
          color: "#FA8C16",
        };
      case "error":
        return {
          backgroundColor: "rgba(255, 80, 80, 0.2)",
          color: "#FF5050",
        };
      case "info":
        return {
          backgroundColor: "rgba(29, 74, 136, 0.1)",
          color: "#1D4A88",
        };
      case "default":
        return {
          backgroundColor: "#F4F6F9",
          color: "#5F6368",
        };
      default:
        return {
          backgroundColor: "#F4F6F9",
          color: "#5F6368",
        };
    }
  };

  const variantStyles = getVariantStyles(variant);

  return (
    <div
      className={clsx(
        "orion-status-pill",
        `orion-status-pill--${variant}`,
        className
      )}
      style={{
        backgroundColor: backgroundColor || variantStyles.backgroundColor,
        color: textColor || variantStyles.color,
        borderRadius: "20px",
        padding: "4px 12px",
        fontSize: "12px",
        fontWeight: 400,
        lineHeight: "1.67",
        fontFamily:
          'Inter, -apple-system, BlinkMacSystemFont, "San Francisco", "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        whiteSpace: "nowrap",
        minHeight: "24px",
      }}
    >
      {children}
    </div>
  );
};

// Convenience components for common status types
export const SuccessPill: React.FC<Omit<StatusPillProps, "variant">> = (
  props
) => <StatusPill variant="success" {...props} />;

export const WarningPill: React.FC<Omit<StatusPillProps, "variant">> = (
  props
) => <StatusPill variant="warning" {...props} />;

export const ErrorPill: React.FC<Omit<StatusPillProps, "variant">> = (
  props
) => <StatusPill variant="error" {...props} />;

export const InfoPill: React.FC<Omit<StatusPillProps, "variant">> = (props) => (
  <StatusPill variant="info" {...props} />
);

// Predefined status pills matching Figma examples
export const ActivePill: React.FC<
  Omit<StatusPillProps, "variant" | "children">
> = (props) => (
  <StatusPill variant="success" {...props}>
    Active
  </StatusPill>
);

export const DraftPill: React.FC<
  Omit<StatusPillProps, "variant" | "children">
> = (props) => (
  <StatusPill variant="warning" {...props}>
    Draft
  </StatusPill>
);

export const DeletePill: React.FC<
  Omit<StatusPillProps, "variant" | "children">
> = (props) => (
  <StatusPill variant="error" {...props}>
    Delete
  </StatusPill>
);
